<template>
  <BaseDialog
    title="新增車牌"
    :btn1="displayText.btn.confirm"
    :close-on-click-modal="false"
    width="610px"
    :btn1-loading="loading"
    @cancel="$emit('close')"
    @close="$emit('close')"
    @confirm="onConfirm"
  >
    <!-- 移除「綁定既有車牌」選項,僅保留「新增車牌」功能 -->

    <BobForm
      :formRef.sync="formRef"
      :model="formData"
      :fields="displayFields"
      :rules="formRules"
      label-position="top"
    />
  </BaseDialog>
</template>

<script>
import BobForm from '@/components/BobForm.vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { useBaseForm } from '@/use/useForm'
import { noEmptyRules, licensePlateRules } from '@/validation'
import { find, uniqBy, filter, uniq, map, get } from 'lodash'
import { CreateKeeperVehicle, CheckPlateAvailability } from '@/api/keeper'

import { computed, defineComponent, provide, ref, toRef } from 'vue'
import notifyMessage from '@/config/notifyMessage'
import { useShop } from '@/use/shop'
import formUtils from '@/utils/form'

export default defineComponent({
  name: 'AppointmentCreateVehicleInfoModal',
  components: { BaseDialog, BobForm },
  props: {
    memberId: {
      type: String,
    },
    memberVehicleList: {
      type: Array,
      default: () => [],
    },
    vehicleModelSetData: {
      type: Array,
      default: () => [],
    },
    allVehicleList: {
      type: Array,
      default: () => [],
    },
  },
  setup (props, { emit }) {
    const { formData, formRef, initFormData, checkForm, loading } = useBaseForm()
    const { shopId } = useShop()
    const actionMode = ref('create') // 固定為 'create',不再支援 binding 模式

    // 車牌驗證狀態
    const licensePlateValidation = ref({
      isChecking: false,
      error: null,
    })

    initFormData({
      licensePlate: undefined,
      brand: undefined,
      model: undefined,
      size: undefined,
      order: 100,
    })

    // 使用 toRef 將 formData 的屬性轉為獨立的 ref,避免整個 formData 的響應式觸發重新渲染
    const brandRef = toRef(formData, 'brand')
    const modelRef = toRef(formData, 'model')
    const sizeRef = toRef(formData, 'size')
    const licensePlateRef = toRef(formData, 'licensePlate')

    // ?? 顯示的品牌選項
    const displayVehicleBrandOptions = computed(() => {
      return uniqBy(map(props.vehicleModelSetData, (item) => {
        return {
          brandName: get(item, 'brandName'),
          brandCode: get(item, 'brandCode'),
        }
      }), 'brandCode')
    })

    // ?? 顯示的型號選項
    const displayVehicleModelOptions = computed(() => {
      const filtered = filter(props.vehicleModelSetData, {
        brandCode: formData.brand,
      })

      return uniqBy(map(filtered, (item) => {
        return {
          modelName: get(item, 'typeName'),
          modelCode: get(item, 'typeCode'),
          size: get(item, 'size'),
        }
      }), 'modelCode')
    })

    // ?? 顯示的尺寸選項
    const displayVehicleSizeOptions = computed(() => {
      // 顯示所有尺寸可能選項，因為後台可以隨意更改尺寸，不需受車型限制
      return map(uniq(map(props.vehicleModelSetData, 'size')), (size) => {
        return {
          label: size,
          value: size,
        }
      })
    })

    // ?? disabled 控制
    const disabledControl = computed(() => {
      const controls = {
        formItem: {
          brand: false, // 不再支援 binding 模式,永遠可編輯
          model: !formData.brand,
          size: false,
        },
      }

      return controls
    })

    const displayFields = computed(() => {
      const fields = [
        {
          prop: 'brand',
          label: '品牌',
          body: {
            component: () => import('@/components/Select/BaseDataSelect.vue'),
            props: {
              value: brandRef,
              dataList: displayVehicleBrandOptions.value,
              labelKey: 'brandName',
              valueKey: 'brandCode',
              objKey: 'brandCode',
              disabled: disabledControl.value.formItem.brand,
              style: {
                width: '560px',
              },
            },
            on: {
              'update:value': (value) => {
                formData.brand = value
              },
              change: () => {
                formData.model = undefined
              },
            },
          },
        },
        {
          prop: 'model',
          label: '型號',
          body: {
            component: () => import('@/components/Select/BaseDataSelect.vue'),
            props: {
              value: modelRef,
              dataList: displayVehicleModelOptions.value,
              labelKey: 'modelName',
              valueKey: 'modelCode',
              objKey: 'modelCode',
              disabled: disabledControl.value.formItem.model,
              style: {
                width: '560px',
              },
            },
            on: {
              'update:value': (value) => {
                formData.model = value
              },
              change: (value) => {
                formData.size = get(find(displayVehicleModelOptions.value, { modelCode: value }), 'size')
              },
            },
          },
        },
        {
          prop: 'size',
          label: '尺寸',
          body: {
            component: () => import('@/components/Select/BaseDataSelect.vue'),
            props: {
              value: sizeRef,
              dataList: displayVehicleSizeOptions.value,
              labelKey: 'label',
              valueKey: 'value',
              objKey: 'value',
              disabled: disabledControl.value.formItem.size,
              style: {
                width: '560px',
              },
            },
            on: {
              'update:value': (value) => {
                formData.size = value
              },
            },
          },
        },
        {
          prop: 'order',
          label: '排序',
          component: () => import('@/components/FormItem/FormItemTooltip.vue'),
          props: {
            tooltip: '排序第一的車牌會是該會員的預設車牌',
          },
          body: {
            component: 'BaseElInput',
            props: {
              type: 'number',
              step: 1,
              min: 0,
              max: 9999,
            },
          },
        },
      ]

      // 車牌號碼 - 固定為新增模式,添加即時驗證
      fields.unshift({
        prop: 'licensePlate',
        label: '車牌',
        body: {
          component: () => import('@/components/Input/LicensePlateInput.vue'),
          props: {
            value: licensePlateRef,
            validateStatus: licensePlateValidation.value.error ? 'error' : '',
            help: licensePlateValidation.value.error || '',
            loading: licensePlateValidation.value.isChecking,
          },
          on: {
            'update:value': (value) => {
              formData.licensePlate = value
            },
            blur: () => {
              if (formData.licensePlate && formData.licensePlate.trim()) {
                checkLicensePlateExists(formData.licensePlate)
              }
            },
          },
        },
      })

      return fields
    })

    const formRules = computed(() => {
      const rules = {
        licensePlate: [noEmptyRules(), licensePlateRules()],
        size: [noEmptyRules()],
        order: [noEmptyRules()],
      }

      // 添加車牌重複檢查規則
      rules.licensePlate.push({
        validator: (rule, value, callback) => {
          if (licensePlateValidation.value.error) {
            callback(new Error(licensePlateValidation.value.error))
          } else {
            callback()
          }
        },
        trigger: 'blur',
      })

      return rules
    })

    const displayText = computed(() => {
      const text = {
        btn: {
          confirm: '新增並綁定車牌',
        },
      }

      return text
    })

    const submitPayload = computed(() => {
      const payload = {
        shopId: shopId.value,
        plate: formData.licensePlate, // 車牌已包含 "-" 符號
        brandCode: formData.brand || undefined,
        typeCode: formData.model || undefined,
        size: formData.size,
        order: formData.order,
        bindingMemberIds: [props.memberId], // 固定為 create 模式
      }

      return payload
    })

    const onConfirm = async () => {
      if (loading.value) return
      loading.value = true

      const isValid = await checkForm(formRef.value)
      if (!isValid) {
        loading.value = false
        return
      }

      const [res, errMsg] = await CreateKeeperVehicle(submitPayload.value)
      loading.value = false

      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      window.$message.success(notifyMessage.createSuccess)

      emit('refresh')
      emit('added', get(res, 'keeperVehicle'))
      emit('close')
    }

    const onActionModeChange = () => {
      // 保留此函數以避免破壞既有邏輯,但不再使用
      formData.vehicle = undefined
      formData.licensePlate = undefined
      formData.brand = undefined
      formData.model = undefined
      formData.size = undefined
    }

    // 檢查車牌號碼是否重複
    const checkLicensePlateExists = async (plate) => {
      if (!plate || plate.trim() === '') {
        licensePlateValidation.value.error = null
        return
      }

      licensePlateValidation.value.isChecking = true
      licensePlateValidation.value.error = null

      // 車牌格式驗證 (必須包含 "-")
      if (!plate.includes('-')) {
        licensePlateValidation.value.isChecking = false
        licensePlateValidation.value.error = '車牌格式錯誤，必須包含 "-" 符號'
        formUtils.validateField(formRef.value, 'licensePlate')
        return
      }

      const plateToCheck = plate.trim()

      // 1. 檢查該會員是否已綁定此車牌
      const memberHasPlate = find(props.memberVehicleList, { plate: plateToCheck })
      if (memberHasPlate) {
        licensePlateValidation.value.isChecking = false
        licensePlateValidation.value.error = '此車牌號碼已綁定於該會員'
        formUtils.validateField(formRef.value, 'licensePlate')
        return
      }

      // 2. 使用新的 CheckPlateAvailability API 檢查車牌是否已被使用
      const [res, err] = await CheckPlateAvailability({
        shopId: shopId.value,
        plate: plateToCheck,
      })
      licensePlateValidation.value.isChecking = false

      if (err) {
        // 404 錯誤表示車牌未被使用（可以使用）
        if (err.status === 404 || err.response?.status === 404) {
          licensePlateValidation.value.error = null
          return
        }
        // 其他錯誤
        console.error('車牌驗證錯誤:', err)
        window.$message.error('車牌驗證失敗，請稍後再試')
        return
      }

      // 有回傳資料表示車牌已被使用
      if (res && res.members && res.members.length > 0) {
        const member = res.members[0] // 取得第一位綁定會員
        // 檢查是否為當前會員
        if (member.id !== props.memberId) {
          licensePlateValidation.value.error = `此車牌已被 ${member.name}(${member.phone}) 綁定，請至會員管理解除綁定或輸入其他車牌。`
        } else {
          // 車牌已被當前會員綁定，允許
          licensePlateValidation.value.error = null
        }
        formUtils.validateField(formRef.value, 'licensePlate')
      }
    }

    provide('formData', formData)

    return {
      formRef,
      formData,
      loading,
      displayFields,
      displayText,
      actionMode,
      formRules,
      onConfirm,
      onActionModeChange,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
